import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './OrderForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function OrderForm({ onSubmit, onCancel, catalogId }) {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    comment: ''
  });
  const [errors, setErrors] = useState({});
  const [loadingUser, setLoadingUser] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]);

  // Получаем user_id и данные пользователя из WebAppData
  useEffect(() => {
    const debugMessages = [];
    debugMessages.push('🔍 OrderForm: Начало загрузки данных пользователя');
    
    // Функция для получения данных пользователя из WebAppData
    const getUserDataFromWebAppData = () => {
      try {
        const hash = window.location.hash;
        debugMessages.push(`🔍 Hash: ${hash.substring(0, 100)}...`);
        
        if (hash.includes('WebAppData=')) {
          debugMessages.push('✅ WebAppData найден в hash');
          const hashParams = new URLSearchParams(hash.substring(1));
          const webAppData = hashParams.get('WebAppData');
          if (webAppData) {
            debugMessages.push(`✅ WebAppData извлечен, длина: ${webAppData.length}`);
            const webAppParams = new URLSearchParams(decodeURIComponent(webAppData));
            const userData = webAppParams.get('user');
            if (userData) {
              debugMessages.push(`✅ user данные найдены в WebAppData`);
              try {
                const user = JSON.parse(userData);
                debugMessages.push(`✅ JSON распарсен, user.id: ${user.id}`);
                debugMessages.push(`🔍 Доступные поля: ${Object.keys(user).join(', ')}`);
                
                if (user && user.id) {
                  console.log('OrderForm: User data from WebAppData:', user);
                  console.log('OrderForm: Available user fields:', Object.keys(user));
                  // Формируем имя из first_name и last_name
                  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
                  
                  // MAX может не передавать phone в WebAppData, пробуем разные варианты
                  const phone = user.phone || user.phone_number || user.mobile || '';
                  
                  debugMessages.push(`✅ Имя: ${fullName || user.first_name || 'не найдено'}`);
                  debugMessages.push(`✅ Телефон: ${phone || 'не найден'}`);
                  
                  setDebugInfo(debugMessages);
                  
                  return {
                    userId: String(user.id),
                    name: fullName || user.first_name || '',
                    phone: phone
                  };
                }
              } catch (e) {
                debugMessages.push(`❌ Ошибка парсинга JSON: ${e.message}`);
                console.error('OrderForm: Error parsing user from WebAppData:', e);
              }
            } else {
              debugMessages.push('⚠️ user данные не найдены в WebAppData');
            }
          } else {
            debugMessages.push('⚠️ WebAppData не извлечен из hash');
          }
        } else {
          debugMessages.push('⚠️ WebAppData не найден в hash');
        }
      } catch (e) {
        debugMessages.push(`❌ Ошибка обработки WebAppData: ${e.message}`);
        console.error('OrderForm: Error processing WebAppData:', e);
      }
      setDebugInfo(debugMessages);
      return null;
    };
    
    // Сначала пробуем получить данные из WebAppData (самый надежный способ)
    const webAppUserData = getUserDataFromWebAppData();
    
    if (webAppUserData) {
      // Автозаполняем форму данными из WebAppData
      const newDebugInfo = [...debugInfo];
      newDebugInfo.push(`✅ Данные из WebAppData получены: userId=${webAppUserData.userId}, name=${webAppUserData.name}, phone=${webAppUserData.phone || 'нет'}`);
      setDebugInfo(newDebugInfo);
      
      console.log('OrderForm: Auto-filling form with:', webAppUserData);
      setFormData(prev => {
        const newData = {
          ...prev,
          name: webAppUserData.name || prev.name,
          phone: webAppUserData.phone || prev.phone
        };
        newDebugInfo.push(`✅ Форма заполнена: name="${newData.name}", phone="${newData.phone}"`);
        setDebugInfo(newDebugInfo);
        return newData;
      });
      
      // Если phone не найден в WebAppData, пробуем запросить из API
      // API возвращает данные из базы данных (сохраненные ботом из webhook)
      if (!webAppUserData.phone && webAppUserData.userId) {
        const apiDebugInfo = [...newDebugInfo];
        apiDebugInfo.push(`📡 Телефон не найден в WebAppData, запрашиваю из API: ${API_URL}/users/${webAppUserData.userId}`);
        setDebugInfo(apiDebugInfo);
        
        console.log('OrderForm: Phone not found in WebAppData, requesting from API...');
        setLoadingUser(true);
        axios.get(`${API_URL}/users/${webAppUserData.userId}`)
          .then(response => {
            const userData = response.data;
            const apiResponseInfo = [...apiDebugInfo];
            apiResponseInfo.push(`✅ Ответ от API получен:`, JSON.stringify(userData));
            setDebugInfo(apiResponseInfo);
            
            console.log('OrderForm: User data from API (for phone):', userData);
            if (userData.phone) {
              setFormData(prev => {
                const updated = { ...prev, phone: userData.phone };
                apiResponseInfo.push(`✅ Телефон обновлен из API: "${userData.phone}"`);
                setDebugInfo(apiResponseInfo);
                return updated;
              });
            } else {
              apiResponseInfo.push(`⚠️ Телефон не найден в ответе API`);
              setDebugInfo(apiResponseInfo);
            }
            // Если имя не было заполнено из WebAppData, заполняем из API
            if (!webAppUserData.name && userData.name) {
              setFormData(prev => {
                const updated = { ...prev, name: userData.name };
                apiResponseInfo.push(`✅ Имя обновлено из API: "${userData.name}"`);
                setDebugInfo(apiResponseInfo);
                return updated;
              });
            }
            setLoadingUser(false);
          })
          .catch(error => {
            const errorInfo = [...apiDebugInfo];
            errorInfo.push(`❌ Ошибка запроса к API: ${error.message}`);
            if (error.response) {
              errorInfo.push(`❌ Статус: ${error.response.status}, Данные: ${JSON.stringify(error.response.data)}`);
            }
            setDebugInfo(errorInfo);
            console.error('OrderForm: Error loading phone from API:', error);
            setLoadingUser(false);
          });
      } else {
        setLoadingUser(false);
      }
      return;
    }
    
    // Если WebAppData не доступен, пробуем получить user_id и запросить данные из API
    const getUserId = () => {
      // Пробуем получить из MAX WebApp API
      if (window.WebApp && window.WebApp.initDataUnsafe && window.WebApp.initDataUnsafe.user) {
        const maxUserId = window.WebApp.initDataUnsafe.user.id;
        if (maxUserId) {
          return String(maxUserId);
        }
      }
      
      // Пробуем получить из URL параметров
      return searchParams.get('user_id') || 
             new URLSearchParams(window.location.search).get('user_id') ||
             new URLSearchParams(window.location.hash.split('?')[1] || '').get('user_id');
    };
    
    const userId = getUserId();
    
    if (userId) {
      const apiDebugInfo = [...debugInfo];
      apiDebugInfo.push(`📡 WebAppData не найден, запрашиваю данные из API: ${API_URL}/users/${userId}`);
      setDebugInfo(apiDebugInfo);
      
      setLoadingUser(true);
      // Запрашиваем данные пользователя из API (резервный способ)
      axios.get(`${API_URL}/users/${userId}`)
        .then(response => {
          const userData = response.data;
          const responseInfo = [...apiDebugInfo];
          responseInfo.push(`✅ Ответ от API получен:`, JSON.stringify(userData));
          setDebugInfo(responseInfo);
          
          console.log('OrderForm: User data loaded from API:', userData);
          setFormData(prev => {
            const newData = {
              ...prev,
              name: userData.name || prev.name,
              phone: userData.phone || prev.phone
            };
            responseInfo.push(`✅ Форма заполнена из API: name="${newData.name}", phone="${newData.phone}"`);
            setDebugInfo(responseInfo);
            return newData;
          });
          setLoadingUser(false);
        })
        .catch(error => {
          const errorInfo = [...apiDebugInfo];
          errorInfo.push(`❌ Ошибка запроса к API: ${error.message}`);
          if (error.response) {
            errorInfo.push(`❌ Статус: ${error.response.status}, Данные: ${JSON.stringify(error.response.data)}`);
          }
          setDebugInfo(errorInfo);
          console.error('Error loading user data from API:', error);
          setLoadingUser(false);
          // Продолжаем работу без автозаполнения
        });
    } else {
      const noUserIdInfo = [...debugInfo];
      noUserIdInfo.push(`⚠️ user_id не найден ни в WebAppData, ни в URL`);
      setDebugInfo(noUserIdInfo);
      setLoadingUser(false);
    }
  }, [searchParams]);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Введите имя';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Введите телефон';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Некорректный формат телефона';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Введите адрес доставки';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="order-form-container">
      {/* Визуальная отладочная информация - только в режиме разработки */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          padding: '10px',
          margin: '10px',
          background: '#f0f0f0',
          border: '1px solid #ccc',
          borderRadius: '5px',
          fontSize: '11px',
          maxHeight: '200px',
          overflow: 'auto'
        }}>
          <strong>🔍 Отладка загрузки данных:</strong>
          <button 
            onClick={() => setDebugInfo([])}
            style={{ marginLeft: '10px', padding: '2px 8px', fontSize: '10px' }}
          >
            Очистить
          </button>
          {debugInfo.length > 0 ? (
            <div style={{ marginTop: '10px' }}>
              {debugInfo.map((info, idx) => (
                <div key={idx} style={{ marginBottom: '3px', fontFamily: 'monospace' }}>
                  {info}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ marginTop: '10px', color: '#666' }}>Нет данных отладки</div>
          )}
          {loadingUser && (
            <div style={{ marginTop: '10px', color: '#0066cc' }}>⏳ Загрузка данных пользователя...</div>
          )}
        </div>
      )}
      <div className="order-form-header">
        <h2>Оформление заказа</h2>
        <button className="close-button" onClick={onCancel}>✕</button>
      </div>
      
      <form onSubmit={handleSubmit} className="order-form">
        <div className="form-group">
          <label htmlFor="name">Имя *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={errors.name ? 'error' : ''}
            placeholder={loadingUser ? "Загрузка..." : "Введите ваше имя"}
            disabled={loadingUser}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">Телефон *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={errors.phone ? 'error' : ''}
            placeholder={loadingUser ? "Загрузка..." : "+7 (999) 123-45-67"}
            disabled={loadingUser}
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="address">Адрес доставки *</label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className={errors.address ? 'error' : ''}
            rows="3"
            placeholder="Введите адрес доставки"
            disabled={loadingUser}
          />
          {errors.address && <span className="error-message">{errors.address}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="comment">Комментарий к заказу</label>
          <textarea
            id="comment"
            name="comment"
            value={formData.comment}
            onChange={handleChange}
            rows="4"
            placeholder="Дополнительная информация к заказу (необязательно)"
          />
        </div>
        
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onCancel}>
            Отмена
          </button>
          <button type="submit" className="submit-button">
            Подтвердить заказ
          </button>
        </div>
      </form>
    </div>
  );
}

export default OrderForm;

